import { useCallback, useEffect, useState, type DragEvent, type FormEvent } from 'react'
import { ChevronLeft, ChevronRight, ExternalLink, GripVertical, ImagePlus, Pencil, Pin, Plus, Trash2, X } from 'lucide-react'
import { pb } from '../../lib/pocketbase'
import { BIO_CATEGORIES } from './categories'
import type { BioItem, BioItemKind } from './types'
import { bioItemImageUrl, normalizeBioLink } from './utils'

type EditorState = { kind: BioItemKind; item?: BioItem } | null
type BannerInput = 'image' | 'html'
const POSTS_PER_PAGE = 24

export function LinkBioAdminPage() {
  const [banners, setBanners] = useState<BioItem[]>([])
  const [posts, setPosts] = useState<BioItem[]>([])
  const [postPage, setPostPage] = useState(1)
  const [totalPosts, setTotalPosts] = useState(0)
  const [loading, setLoading] = useState(true)
  const [postsLoading, setPostsLoading] = useState(false)
  const [editor, setEditor] = useState<EditorState>(null)
  const [deleting, setDeleting] = useState<BioItem | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const [dragOverId, setDragOverId] = useState<string | null>(null)
  const [featuredCount, setFeaturedCount] = useState(0)

  const totalPostPages = Math.max(1, Math.ceil(totalPosts / POSTS_PER_PAGE))
  const pageStart = totalPosts ? (postPage - 1) * POSTS_PER_PAGE + 1 : 0
  const pageEnd = Math.min(postPage * POSTS_PER_PAGE, totalPosts)

  const loadPosts = useCallback(async (page: number) => {
    setPostsLoading(true)
    try {
      const response = await pb.collection('link_bio_items').getList<BioItem>(page, POSTS_PER_PAGE, { filter: 'kind = "post"', sort: 'position' })
      setPosts(response.items)
      setPostPage(response.page)
      setTotalPosts(response.totalItems)
    } finally {
      setPostsLoading(false)
    }
  }, [])

  const loadFeaturedCount = useCallback(async () => {
    const response = await pb.collection('link_bio_items').getList<BioItem>(1, 1, { filter: 'kind = "post" && featured = true' })
    setFeaturedCount(response.totalItems)
  }, [])

  const goToPostPage = async (page: number) => {
    if (busy || postsLoading) return
    setError('')
    try {
      await loadPosts(Math.min(Math.max(page, 1), totalPostPages))
    } catch {
      setError('Não foi possível carregar esta página do feed.')
    }
  }

  useEffect(() => {
    Promise.all([
      pb.collection('link_bio_items').getFullList<BioItem>({ filter: 'kind = "banner"', sort: 'position' }).then(setBanners),
      loadPosts(1),
      loadFeaturedCount(),
    ])
      .catch(() => setError('Não foi possível carregar o Link Bio.'))
      .finally(() => setLoading(false))
  }, [loadFeaturedCount, loadPosts])

  const saveItem = async (data: { title: string; link: string; file?: File; html?: string; bannerInput?: BannerInput; tags?: string[] }) => {
    if (!editor) return
    setBusy(true)
    setError('')
    try {
      const payload: Record<string, unknown> = {
        kind: editor.kind,
        title: data.title,
        link: data.link.trim() ? normalizeBioLink(data.link) : '',
      }
      if (data.file) payload.image = data.file
      if (editor.kind === 'banner') payload.html = data.bannerInput === 'html' ? data.html?.trim() ?? '' : ''
      if (editor.kind === 'post') payload.tags = data.tags ?? []
      if (!editor.item && editor.kind === 'post') payload.featured = false

      let saved: BioItem
      if (editor.item) {
        saved = await pb.collection('link_bio_items').update<BioItem>(editor.item.id, payload)
        if (saved.kind === 'banner') {
          setBanners((current) => current.map((item) => (item.id === saved.id ? saved : item)))
        } else {
          setPosts((current) => current.map((item) => (item.id === saved.id ? saved : item)))
        }
      } else {
        const siblings = editor.kind === 'banner'
          ? banners
          : (await pb.collection('link_bio_items').getList<BioItem>(1, 1, { filter: 'kind = "post"', sort: '-position' })).items
        payload.position = siblings.length ? Math.max(...siblings.map((item) => item.position)) + 1000 : 1000
        saved = await pb.collection('link_bio_items').create<BioItem>(payload)
        if (saved.kind === 'banner') {
          setBanners((current) => [...current, saved].sort((a, b) => a.position - b.position))
        } else {
          const nextLastPage = Math.max(1, Math.ceil((totalPosts + 1) / POSTS_PER_PAGE))
          await loadPosts(nextLastPage)
        }
      }
      setEditor(null)
    } catch {
      setError('Não foi possível salvar este item.')
    } finally {
      setBusy(false)
    }
  }

  const toggleFeatured = async (item: BioItem) => {
    if (item.kind !== 'post') return
    if (!item.featured && featuredCount >= 3) {
      setError('Você pode destacar no máximo 3 matérias.')
      return
    }
    setBusy(true)
    setError('')
    try {
      const saved = await pb.collection('link_bio_items').update<BioItem>(item.id, { featured: !item.featured })
      setPosts((current) => current.map((currentItem) => currentItem.id === saved.id ? saved : currentItem))
      setFeaturedCount((current) => current + (saved.featured ? 1 : -1))
    } catch {
      setError('Não foi possível atualizar o destaque.')
    } finally {
      setBusy(false)
    }
  }

  const handlePostDrop = async (targetId: string) => {
    if (!draggingId || draggingId === targetId || busy) return
    const fromIndex = posts.findIndex((item) => item.id === draggingId)
    const toIndex = posts.findIndex((item) => item.id === targetId)
    if (fromIndex < 0 || toIndex < 0) return
    const reorderedPosts = [...posts]
    const [moved] = reorderedPosts.splice(fromIndex, 1)
    reorderedPosts.splice(toIndex, 0, moved)
    setDraggingId(null)
    setDragOverId(null)
    setBusy(true)
    setError('')
    try {
      const [beforeResponse, afterResponse] = await Promise.all([
        pageStart > 1 ? pb.collection('link_bio_items').getList<BioItem>(pageStart - 1, 1, { filter: 'kind = "post"', sort: 'position' }) : Promise.resolve(null),
        pageEnd < totalPosts ? pb.collection('link_bio_items').getList<BioItem>(pageEnd + 1, 1, { filter: 'kind = "post"', sort: 'position' }) : Promise.resolve(null),
      ])
      const before = beforeResponse?.items[0]?.position
      const after = afterResponse?.items[0]?.position
      const positions = calculatePagePositions(reorderedPosts.length, before, after)
      setPosts(reorderedPosts.map((item, index) => ({ ...item, position: positions[index] })))
      await Promise.all(reorderedPosts.map((item, index) => pb.collection('link_bio_items').update(item.id, { position: positions[index] })))
      await loadPosts(postPage)
    } catch {
      setError('A ordem foi exibida localmente, mas não foi possível salvá-la.')
    } finally {
      setBusy(false)
    }
  }

  const deleteItem = async () => {
    if (!deleting) return
    setBusy(true)
    setError('')
    try {
      await pb.collection('link_bio_items').delete(deleting.id)
      if (deleting.kind === 'banner') {
        setBanners((current) => current.filter((item) => item.id !== deleting.id))
      } else {
        if (deleting.featured) setFeaturedCount((current) => Math.max(0, current - 1))
        const nextTotal = Math.max(0, totalPosts - 1)
        const nextPage = Math.min(postPage, Math.max(1, Math.ceil(nextTotal / POSTS_PER_PAGE)))
        await loadPosts(nextPage)
      }
      setDeleting(null)
    } catch {
      setError('Não foi possível apagar este item.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="bio-admin">
      <div className="bio-admin-header">
        <div>
          <span className="bio-admin-kicker">LORENA MAGAZINE</span>
          <h1>Gerenciador do Link Bio</h1>
          <p>Organize os anúncios e as capas que aparecem na página pública.</p>
        </div>
        <a className="bio-admin-preview-link" href="/bio" target="_blank" rel="noreferrer">Visualizar página <ExternalLink size={15} /></a>
      </div>

      {error && !editor && !deleting && <p className="bio-admin-error">{error}</p>}
      {loading ? <p className="bio-admin-loading">Carregando…</p> : (
        <>
          <AdminSection title="Banners de anúncio" count={banners.length} hint="Ideal: 1600 × 500 px · proporção 16:5" actionLabel="Adicionar banner" onAdd={() => { setError(''); setEditor({ kind: 'banner' }) }}>
            {banners.length ? (
              <div className="bio-admin-banner-grid">
                {banners.map((item) => <AdminItemCard key={item.id} item={item} variant="banner" onEdit={() => { setError(''); setEditor({ kind: 'banner', item }) }} onDelete={() => { setError(''); setDeleting(item) }} />)}
              </div>
            ) : <EmptyState text="Nenhum anúncio cadastrado." />}
          </AdminSection>

          <AdminSection title="Feed de matérias" count={totalPosts} hint={`Ideal: 1080 × 1440 px · proporção 3:4 · ${featuredCount}/3 destaques fixados · arraste para ordenar`} actionLabel="Adicionar capa" onAdd={() => { setError(''); setEditor({ kind: 'post' }) }}>
            {posts.length ? (
              <>
                <div className={`bio-admin-post-grid${postsLoading ? ' is-loading' : ''}`}>
                  {posts.map((item) => <AdminItemCard key={item.id} item={item} variant="post" busy={busy} isDragging={draggingId === item.id} isDragOver={dragOverId === item.id} onDragStart={(event) => { event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData('text/plain', item.id); setDraggingId(item.id) }} onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = 'move'; if (dragOverId !== item.id) setDragOverId(item.id) }} onDrop={(event) => { event.preventDefault(); void handlePostDrop(item.id) }} onDragEnd={() => { setDraggingId(null); setDragOverId(null) }} onToggleFeatured={() => toggleFeatured(item)} onEdit={() => { setError(''); setEditor({ kind: 'post', item }) }} onDelete={() => { setError(''); setDeleting(item) }} />)}
                </div>
                <div className="bio-admin-pagination" aria-label="Paginação do feed">
                  <span>{pageStart}-{pageEnd} de {totalPosts} capas</span>
                  <div>
                    <button type="button" disabled={postPage <= 1 || postsLoading || busy} onClick={() => void goToPostPage(postPage - 1)} aria-label="Página anterior"><ChevronLeft size={16} /></button>
                    <strong>Página {postPage} de {totalPostPages}</strong>
                    <button type="button" disabled={postPage >= totalPostPages || postsLoading || busy} onClick={() => void goToPostPage(postPage + 1)} aria-label="Próxima página"><ChevronRight size={16} /></button>
                  </div>
                </div>
              </>
            ) : <EmptyState text="Nenhuma matéria cadastrada no feed." />}
          </AdminSection>
        </>
      )}

      {editor && <ItemEditorModal state={editor} busy={busy} error={error} onClose={() => !busy && setEditor(null)} onSave={saveItem} />}

      {deleting && (
        <div className="modal-backdrop" onClick={() => !busy && setDeleting(null)}>
          <div className="modal-card bio-item-modal" role="dialog" aria-modal="true" aria-labelledby="delete-bio-item" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header"><h2 id="delete-bio-item">Apagar {deleting.kind === 'banner' ? 'banner' : 'capa'}?</h2></div>
            <p className="materia-delete-copy">Este item será removido imediatamente da página pública.</p>
            {error && <p className="materia-action-error">{error}</p>}
            <div className="materia-action-buttons">
              <button type="button" className="secondary" disabled={busy} onClick={() => setDeleting(null)}>Cancelar</button>
              <button type="button" className="danger-button" disabled={busy} onClick={deleteItem}>{busy ? 'Apagando…' : 'Apagar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function calculatePagePositions(count: number, before?: number, after?: number) {
  if (before !== undefined && after !== undefined) {
    const step = (after - before) / (count + 1)
    return Array.from({ length: count }, (_, index) => before + step * (index + 1))
  }
  if (after !== undefined) return Array.from({ length: count }, (_, index) => after - (count - index) * 1000)
  if (before !== undefined) return Array.from({ length: count }, (_, index) => before + (index + 1) * 1000)
  return Array.from({ length: count }, (_, index) => (index + 1) * 1000)
}

function AdminSection({ title, count, hint, actionLabel, onAdd, children }: { title: string; count: number; hint: string; actionLabel: string; onAdd: () => void; children: React.ReactNode }) {
  return <section className="bio-admin-section">
    <div className="bio-admin-section-header">
      <div className="bio-admin-section-title"><div><h2>{title}</h2><span>{String(count).padStart(2, '0')} ITENS</span></div><p>{hint}</p></div>
      <button type="button" onClick={onAdd}><Plus size={15} />{actionLabel}</button>
    </div>
    {children}
  </section>
}

function EmptyState({ text }: { text: string }) {
  return <div className="bio-admin-empty"><ImagePlus size={24} /><span>{text}</span></div>
}

function AdminItemCard({ item, variant, busy, isDragging, isDragOver, onDragStart, onDragOver, onDrop, onDragEnd, onToggleFeatured, onEdit, onDelete }: { item: BioItem; variant: BioItemKind; busy?: boolean; isDragging?: boolean; isDragOver?: boolean; onDragStart?: (event: DragEvent<HTMLElement>) => void; onDragOver?: (event: DragEvent<HTMLElement>) => void; onDrop?: (event: DragEvent<HTMLElement>) => void; onDragEnd?: () => void; onToggleFeatured?: () => void; onEdit: () => void; onDelete: () => void }) {
  return <article className={`bio-admin-item ${variant}${isDragging ? ' is-dragging' : ''}${isDragOver ? ' is-drag-over' : ''}`} draggable={variant === 'post'} onDragStart={onDragStart} onDragOver={onDragOver} onDrop={onDrop} onDragEnd={onDragEnd}>
    {item.html ? <iframe className="bio-admin-html-preview" srcDoc={item.html} title={item.title || 'Banner HTML5'} sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation" /> : <img src={bioItemImageUrl(item)} alt={item.title || ''} />}
    {variant === 'post' && <span className="bio-drag-handle" title="Arrastar para reordenar" aria-hidden="true"><GripVertical size={16} /></span>}
    {item.featured && <span className="bio-featured-badge">DESTAQUE</span>}
    <div className="bio-admin-item-actions">
      {variant === 'post' && onToggleFeatured && <button type="button" className={item.featured ? 'is-featured' : ''} disabled={busy} onClick={onToggleFeatured} title={item.featured ? 'Desafixar destaque' : 'Destacar matéria'} aria-label={item.featured ? `Desafixar ${item.title || 'matéria'}` : `Destacar ${item.title || 'matéria'}`}><Pin size={15} fill={item.featured ? 'currentColor' : 'none'} /></button>}
      <button type="button" onClick={onEdit} title="Editar" aria-label={`Editar ${item.title || 'item'}`}><Pencil size={15} /></button>
      <button type="button" onClick={onDelete} title="Apagar" aria-label={`Apagar ${item.title || 'item'}`}><Trash2 size={15} /></button>
    </div>
    <div className="bio-admin-item-meta">
      <strong>{item.title || (variant === 'banner' ? 'Banner sem título' : 'Matéria sem título')}</strong>
      <span>{item.link.replace(/^https?:\/\//, '')}</span>
    </div>
  </article>
}

function ItemEditorModal({ state, busy, error, onClose, onSave }: { state: Exclude<EditorState, null>; busy: boolean; error: string; onClose: () => void; onSave: (data: { title: string; link: string; file?: File; html?: string; bannerInput?: BannerInput; tags?: string[] }) => void }) {
  const [title, setTitle] = useState(state.item?.title ?? '')
  const [link, setLink] = useState(state.item?.link ?? '')
  const [file, setFile] = useState<File>()
  const [preview, setPreview] = useState(state.item ? bioItemImageUrl(state.item) : '')
  const [bannerInput, setBannerInput] = useState<BannerInput>(state.item?.html ? 'html' : 'image')
  const [html, setHtml] = useState(state.item?.html ?? '')
  const [tags, setTags] = useState<string[]>(state.item?.tags ?? [])

  useEffect(() => () => { if (preview.startsWith('blob:')) URL.revokeObjectURL(preview) }, [preview])

  const selectFile = (selected?: File) => {
    if (!selected) return
    if (preview.startsWith('blob:')) URL.revokeObjectURL(preview)
    setFile(selected)
    setPreview(URL.createObjectURL(selected))
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const valid = state.kind === 'banner' ? (bannerInput === 'html' ? html.trim() : Boolean(file || state.item?.image)) : Boolean(link.trim() && (file || state.item?.image))
    if (!valid) return
    onSave({ title: title.trim(), link: link.trim(), file, html, bannerInput, tags })
  }

  const noun = state.kind === 'banner' ? 'banner' : 'capa'
  return <div className="modal-backdrop" onClick={onClose}>
    <form className="modal-card bio-item-modal" role="dialog" aria-modal="true" aria-labelledby="bio-editor-title" onSubmit={submit} onClick={(event) => event.stopPropagation()}>
      <div className="modal-header">
        <h2 id="bio-editor-title">{state.item ? 'Editar' : 'Adicionar'} {noun}</h2>
        <button type="button" className="bio-modal-close" onClick={onClose} aria-label="Fechar"><X size={18} /></button>
      </div>
      <label htmlFor="bio-item-title">Título <span>(opcional)</span></label>
      <input id="bio-item-title" value={title} onChange={(event) => setTitle(event.target.value)} maxLength={200} placeholder={state.kind === 'banner' ? 'Nome do anúncio' : 'Título da matéria'} />
      <label className="bio-modal-field-label" htmlFor="bio-item-link">Link de destino</label>
      <input id="bio-item-link" value={link} onChange={(event) => setLink(event.target.value)} required={state.kind === 'post' || bannerInput === 'image'} placeholder={state.kind === 'banner' && bannerInput === 'html' ? 'Opcional — o HTML pode conter seu próprio link' : 'https://lorenamagazine.com/...'} inputMode="url" />
      {state.kind === 'post' && <div className="bio-category-editor"><label className="bio-modal-field-label">Categorias</label><div className="bio-category-options">{BIO_CATEGORIES.map((category) => <label key={category}><input type="checkbox" checked={tags.includes(category)} onChange={(event) => setTags((current) => event.target.checked ? [...current, category] : current.filter((tag) => tag !== category))} /><span>{category}</span></label>)}</div></div>}
      {state.kind === 'banner' && <div className="bio-banner-input-switch" role="tablist" aria-label="Formato do banner"><button type="button" className={bannerInput === 'image' ? 'active' : ''} onClick={() => setBannerInput('image')}>Imagem</button><button type="button" className={bannerInput === 'html' ? 'active' : ''} onClick={() => setBannerInput('html')}>HTML5</button></div>}
      {state.kind === 'banner' && bannerInput === 'html' ? <>
        <label className="bio-modal-field-label" htmlFor="bio-item-html">Código HTML5</label>
        <textarea id="bio-item-html" className="bio-html-input" value={html} onChange={(event) => setHtml(event.target.value)} placeholder="Cole aqui o HTML5 do banner, incluindo <style> e links." />
        <p className="field-help">O conteúdo será exibido em uma área isolada. Para melhor resultado, crie o banner em 1600 × 500 px (16:5).</p>
      </> : <>
        <label className="bio-modal-field-label">Imagem {state.item && <span>(opcional para manter a atual)</span>}</label>
        <label className={`bio-image-picker ${state.kind}`}>
          {preview ? <img src={preview} alt="Prévia do arquivo" /> : <><ImagePlus size={26} /><span>Selecionar imagem</span></>}
          <input className="visually-hidden" type="file" accept="image/png,image/jpeg,image/webp" required={!state.item} onChange={(event) => selectFile(event.target.files?.[0])} />
        </label>
        <p className="field-help">{state.kind === 'banner' ? 'Tamanho ideal: 1600 × 500 px (proporção 16:5). Imagens nessa proporção evitam cortes no banner.' : 'Tamanho ideal: 1080 × 1440 px (proporção 3:4). Esta é a proporção exibida no feed.'}</p>
      </>}
      {error && <p className="materia-action-error">{error}</p>}
      <div className="materia-action-buttons">
        <button type="button" className="secondary" disabled={busy} onClick={onClose}>Cancelar</button>
        <button type="submit" disabled={busy || (state.kind === 'post' && (!link.trim() || (!state.item && !file))) || (state.kind === 'banner' && (bannerInput === 'html' ? !html.trim() : (!state.item && !file)))}>{busy ? 'Salvando…' : 'Salvar'}</button>
      </div>
    </form>
  </div>
}
